import React, { useEffect, useMemo, useState } from 'react';

import QueryString from 'qs';
import { omit } from 'ramda';
import { useQuery, useQueryClient } from 'react-query';
import { useAsyncDebounce } from 'react-table';

import agentApplicationsApi from 'api/agents';
import { FETCH_AGENT_APPLICATIONS } from 'constants/queries';
import { AgentApplicationsProps } from 'routes/main/AgentApplications/AgentApplications.props';
import AgentApplicationsView from 'routes/main/AgentApplications/AgentApplications.view';
import { AgentApplicationsRow } from 'types/Agent';

export const TABLE_OPTIONS = {
  page: 1,
  limit: 20,
};

const AgentApplicationsContainer = (
  props: AgentApplicationsProps
): JSX.Element => {
  const [tableOptions, setTableOptions] =
    useState<Record<string, any>>(TABLE_OPTIONS);

  const queryClient = useQueryClient();

  const { isFetching, data, refetch } = useQuery(
    FETCH_AGENT_APPLICATIONS,
    async () =>
      agentApplicationsApi.getAll(
        `${
          props.location.search ? props.location.search + '&' : '?'
        }${QueryString.stringify(tableOptions)}`
      )
  );

  const queries = useMemo(
    () => QueryString.parse(new URLSearchParams(location.search).toString()),
    [location.search]
  );

  useEffect(() => {
    setTableOptions((opts) => ({ ...opts, page: 1 }));
  }, [queries.filter_status, queries.search]);

  const onSearchKey = useAsyncDebounce(
    (searchKey: string) =>
      props.history.push({
        pathname: props.location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...omit(['search'], queries),
            ...(searchKey ? { search: searchKey } : {}),
          }).toString(),
      }),
    600
  );

  const meta = React.useMemo(
    () =>
      data?.meta || {
        count: 0,
        limit: 10,
        page: 1,
      },
    [data]
  );

  const onSetFilter = (value: string) => {
    const toFilterStatus = () => {
      let filterValue = '';
      switch (value) {
        case 'accepted':
          filterValue = 'APPROVED';
          break;
        case 'declined':
          filterValue = 'REJECTED';
          break;
        case 'pending':
          filterValue = 'PENDING';
          break;
        case 'pending_update':
          filterValue = 'PENDING_UPDATE';
          break;
        case 'suspended':
          filterValue = 'SUSPENDED';
          break;
        default:
          break;
      }

      return { filter_status: filterValue };
    };

    props.history.push({
      pathname: props.location.pathname,
      search: '?' + new URLSearchParams(toFilterStatus()).toString(),
    });
  };

  const onClearFilter = () =>
    props.history.push({
      pathname: props.location.pathname,
    });

  const setAgent = (agent?: AgentApplicationsRow) =>
    props.history.push({
      pathname: props.location.pathname,
      search:
        '?' +
        new URLSearchParams({
          ...omit(['id'], queries),
          ...(agent ? { id: agent.id } : {}),
        }).toString(),
    });

  useEffect(() => {
    queryClient.cancelQueries([FETCH_AGENT_APPLICATIONS]);
    setTimeout(refetch, 300);
  }, [props.location.search, tableOptions]);

  return (
    <AgentApplicationsView
      setAgent={setAgent}
      data={data?.data || []}
      meta={meta}
      onSearchKey={onSearchKey}
      isLoading={isFetching}
      onClearFilter={onClearFilter}
      onSetFilter={onSetFilter}
      tableOptions={tableOptions}
      setTableOptions={setTableOptions}
    />
  );
};

export default AgentApplicationsContainer;
